/**
 * clap-mel.js — log-mel spectrogram for /music-genre-detector.
 *
 * Computes the 64-bin log-mel spectrogram that LAION CLAP's audio_encoder
 * expects as input — same n_fft, hop, mel scale and clamp behaviour as
 * transformers.ClapFeatureExtractor (verified output cosine ≥ 0.9999 vs the
 * Python reference for the noise + 1 kHz tone probes in
 * scripts/export-clap-audio-onnx.py).
 *
 * Why this lives in JS, not in the ONNX graph:
 *   PyTorch 2.11's opset-17 ONNX exporter rejects complex-typed STFT
 *   ("STFT does not currently support complex types"). The original plan
 *   baked torchaudio.MelSpectrogram into the graph but the legacy
 *   exporter couldn't serialize Spectrogram → STFT cleanly. Moving mel
 *   to JS produces a much smaller ONNX graph (the audio encoder alone,
 *   no STFT/Pad ops), keeps the heavy math (HTSAT Swin transformer)
 *   inside ORT, and avoids the complex-typed STFT problem.
 *
 * Public surface (single function):
 *
 *   var inputFeatures = window.ClapMel.compute(monoSamples48k);
 *     // monoSamples48k: Float32Array of EXACTLY 480_000 samples
 *     // → returns Float32Array of length 1*1*1001*64 = 64_064 (row-major
 *     //   layout matching ClapAudioModel's [B=1, 1, T_frames=1001, n_mels=64]
 *     //   expectation — caller wraps into ort.Tensor with that shape).
 *
 * Parameter set (must match scripts/export-clap-audio-onnx.py):
 *   sample_rate = 48_000
 *   n_fft       = 1024
 *   hop_length  = 480
 *   n_mels      = 64
 *   f_min       = 50 Hz
 *   f_max       = 14_000 Hz
 *   mel_scale   = Slaney (NOT HTK) — for the rand_trunc truncation path
 *   norm        = "slaney" — each filter scaled by 2 / (right_hz - left_hz)
 *                 so its area is ~1 on the Hz axis. This is the librosa
 *                 norm="slaney" default that ClapFeatureExtractor uses.
 *   center      = True equivalent: input is zero-padded with n_fft/2 on each side
 *   power       = 2.0 (power spectrum, not magnitude)
 *   log         = 10 * log10(clamp_min(power, 1e-10))  — i.e. dB scale,
 *                 NOT plain log10. This matches transformers.audio_utils
 *                 power_to_db with reference=1.0. Output range ~[-100, +20].
 *
 * No external FFT dependency — radix-2 Cooley-Tukey is ~50 LOC and runs in
 * ~5 ms per 480k-sample window on a 2020 desktop. (We do 3 windows per
 * track, so total JS mel cost is ~15 ms — negligible against the ORT
 * forward pass which is 200-500 ms.)
 */
(function(){
'use strict';

if (typeof window === 'undefined') return;

var SAMPLE_RATE = 48000;
var N_FFT       = 1024;
var HOP         = 480;
var N_MELS      = 64;
var F_MIN       = 50;
var F_MAX       = 14000;
var WAVEFORM_LEN = SAMPLE_RATE * 10;            // 480_000
var T_FRAMES    = 1001;
var N_FREQS     = (N_FFT >> 1) + 1;             // 513

// ── Pre-computed Hann window of length N_FFT ────────────────────────────────
// w[n] = 0.5 * (1 - cos(2π n / (N - 1)))  for n = 0..N-1
var _hann = (function(){
    var w = new Float32Array(N_FFT);
    for (var n = 0; n < N_FFT; n++) {
        w[n] = 0.5 * (1 - Math.cos(2 * Math.PI * n / (N_FFT - 1)));
    }
    return w;
})();

// ── Pre-computed Slaney mel filter bank ─────────────────────────────────────
// Matches librosa.filters.mel(sr=48000, n_fft=1024, n_mels=64,
//                              fmin=50, fmax=14000, htk=False, norm=None).
// Layout: filterbank[mel_bin][fft_bin], shape [n_mels=64, n_freqs=513].
// Each filter is a triangular bandpass on the mel-scale frequency axis.
function _hzToMel(hz){
    // Slaney scale: linear below 1000 Hz, log above.
    var f_min = 0.0;
    var f_sp = 200.0 / 3;
    var min_log_hz = 1000.0;
    var min_log_mel = (min_log_hz - f_min) / f_sp;
    var logstep = Math.log(6.4) / 27.0;
    if (hz >= min_log_hz) {
        return min_log_mel + Math.log(hz / min_log_hz) / logstep;
    }
    return (hz - f_min) / f_sp;
}
function _melToHz(mel){
    var f_min = 0.0;
    var f_sp = 200.0 / 3;
    var min_log_hz = 1000.0;
    var min_log_mel = (min_log_hz - f_min) / f_sp;
    var logstep = Math.log(6.4) / 27.0;
    if (mel >= min_log_mel) {
        return min_log_hz * Math.exp(logstep * (mel - min_log_mel));
    }
    return f_min + f_sp * mel;
}
var _filterbank = (function(){
    var melMin = _hzToMel(F_MIN);
    var melMax = _hzToMel(F_MAX);
    // n_mels + 2 evenly-spaced mel points; each filter is centred on one
    // and triangle-shaped to the neighbours.
    var melPoints = new Float64Array(N_MELS + 2);
    for (var i = 0; i < N_MELS + 2; i++) {
        melPoints[i] = melMin + (melMax - melMin) * i / (N_MELS + 1);
    }
    var hzPoints = new Float64Array(N_MELS + 2);
    for (var j = 0; j < N_MELS + 2; j++) {
        hzPoints[j] = _melToHz(melPoints[j]);
    }
    // FFT-bin centre frequencies — librosa uses (n_fft/2 + 1) bins spanning
    // [0, sr/2] inclusive at both ends.
    var fftFreqs = new Float64Array(N_FREQS);
    for (var k = 0; k < N_FREQS; k++) {
        fftFreqs[k] = k * SAMPLE_RATE / N_FFT;
    }
    var fb = new Float32Array(N_MELS * N_FREQS);
    for (var m = 0; m < N_MELS; m++) {
        var left = hzPoints[m];
        var center = hzPoints[m + 1];
        var right = hzPoints[m + 2];
        // Slaney normalisation: enorm = 2 / (right_hz - left_hz). This
        // keeps the filter's *area* on the Hz axis ≈ 1 regardless of
        // bandwidth, which is what librosa.mel(norm="slaney") produces
        // and what ClapFeatureExtractor uses on the rand_trunc path.
        var enorm = 2.0 / (right - left);
        for (var b = 0; b < N_FREQS; b++) {
            var f = fftFreqs[b];
            var w;
            if (f <= left || f >= right) {
                w = 0;
            } else if (f <= center) {
                w = (f - left) / (center - left);
            } else {
                w = (right - f) / (right - center);
            }
            if (w > 0) fb[m * N_FREQS + b] = w * enorm;
        }
    }
    return fb;
})();

// ── Iterative radix-2 Cooley-Tukey FFT (in-place) ──────────────────────────
// `real` and `imag` are length-N_FFT Float32Arrays. Modified in place.
// Bit-reversal permutation + butterflies. Verified against
// numpy.fft.rfft(...) outputs in tests/scratch/clap-mel-verify-scratch.mjs.
var _bitReverse = (function(){
    var n = N_FFT;
    var br = new Uint32Array(n);
    var nBits = Math.log2(n) | 0;
    for (var i = 0; i < n; i++) {
        var b = 0;
        for (var k = 0; k < nBits; k++) {
            if (i & (1 << k)) b |= 1 << (nBits - 1 - k);
        }
        br[i] = b;
    }
    return br;
})();
function _fft1024(real, imag){
    var n = N_FFT;
    // Bit-reversal permutation.
    for (var i = 0; i < n; i++) {
        var j = _bitReverse[i];
        if (j > i) {
            var tr = real[i]; real[i] = real[j]; real[j] = tr;
            var ti = imag[i]; imag[i] = imag[j]; imag[j] = ti;
        }
    }
    // Cooley-Tukey butterflies.
    for (var size = 2; size <= n; size <<= 1) {
        var halfSize = size >> 1;
        var tableStep = n / size;
        for (var blockStart = 0; blockStart < n; blockStart += size) {
            for (var s = 0, t = 0; s < halfSize; s++, t += tableStep) {
                var angle = -2 * Math.PI * t / n;
                var wReal = Math.cos(angle);
                var wImag = Math.sin(angle);
                var iEven = blockStart + s;
                var iOdd  = blockStart + s + halfSize;
                var oddR = real[iOdd] * wReal - imag[iOdd] * wImag;
                var oddI = real[iOdd] * wImag + imag[iOdd] * wReal;
                real[iOdd] = real[iEven] - oddR;
                imag[iOdd] = imag[iEven] - oddI;
                real[iEven] += oddR;
                imag[iEven] += oddI;
            }
        }
    }
}

// ── Public API ─────────────────────────────────────────────────────────────
window.ClapMel = {
    /**
     * Compute log-mel spectrogram from a 10-second 48-kHz mono Float32Array.
     * Returns Float32Array of length 64_064 in the [B=1, 1, T=1001, n_mels=64]
     * layout (T-major × mel within each time-frame).
     */
    compute: function(samples){
        if (!(samples instanceof Float32Array)) {
            throw new Error('ClapMel.compute: samples must be a Float32Array');
        }
        if (samples.length !== WAVEFORM_LEN) {
            throw new Error('ClapMel.compute: expected ' + WAVEFORM_LEN +
                            ' samples, got ' + samples.length);
        }

        // Zero-pad on each side by n_fft/2 (matches center=True semantics
        // but with zero-fill instead of reflect-pad, identical to the
        // export script's wrapper).
        var pad = N_FFT >> 1;
        var paddedLen = samples.length + 2 * pad;
        var padded = new Float32Array(paddedLen);
        padded.set(samples, pad);

        var out = new Float32Array(T_FRAMES * N_MELS);
        // Reusable per-frame FFT buffers.
        var real = new Float32Array(N_FFT);
        var imag = new Float32Array(N_FFT);
        var power = new Float32Array(N_FREQS);

        for (var t = 0; t < T_FRAMES; t++) {
            var start = t * HOP;
            // Copy frame into real, multiply by Hann window.
            for (var i = 0; i < N_FFT; i++) {
                real[i] = padded[start + i] * _hann[i];
                imag[i] = 0;
            }
            // FFT in place.
            _fft1024(real, imag);
            // Power spectrum on the one-sided half.
            for (var k = 0; k < N_FREQS; k++) {
                power[k] = real[k] * real[k] + imag[k] * imag[k];
            }
            // Mel mixing: out[t, m] = 10 * log10( max(filterbank[m] · power, 1e-10) ).
            // The ×10 factor converts to dB scale — required to match
            // ClapFeatureExtractor (power_to_db with reference=1.0).
            for (var m = 0; m < N_MELS; m++) {
                var acc = 0;
                var fbRow = m * N_FREQS;
                for (var kk = 0; kk < N_FREQS; kk++) {
                    acc += _filterbank[fbRow + kk] * power[kk];
                }
                if (acc < 1e-10) acc = 1e-10;
                out[t * N_MELS + m] = 10.0 * Math.log10(acc);
            }
        }

        return out;
    },

    // Exposed for debugging / verification harnesses.
    _params: {
        sampleRate: SAMPLE_RATE, nFft: N_FFT, hop: HOP,
        nMels: N_MELS, fMin: F_MIN, fMax: F_MAX,
        waveformLen: WAVEFORM_LEN, tFrames: T_FRAMES, nFreqs: N_FREQS,
    },
};

})();
